import { API_BASE, api } from "@/lib/apiClient";

export type TicketStatus = "open" | "in_progress" | "waiting" | "resolved" | "closed";
export type TicketUrgency = "low" | "normal" | "high" | "critical";
export type TodoState =
  | "eingang"
  | "unwichtig"
  | "wuensche"
  | "umsetzbar"
  | "wichtig"
  | "in_umsetzung"
  | "erledigt";

export interface SupportTag {
  id: number;
  name: string;
  color?: string | null;
}

export interface SupportTicket {
  id: number;
  title: string;
  status: TicketStatus | string;
  urgency: TicketUrgency | string;
  program: string;
  reporter_display?: string | null;
  reporter_login?: string | null;
  reporter_email?: string | null;
  todo_state?: TodoState | string | null;
  todo_owner_login?: string | null;
  todo_due_at?: string | null;
  todo_note?: string | null;
  message?: string | null;
  tags?: SupportTag[];
  created_at?: string | null;
  updated_at?: string | null;
  /** Pro aktuellem User: Ticket nie gelesen oder seit letztem Lesen aktualisiert */
  is_unread?: boolean;
  /** Private Notiz des aktuellen Users (nur für ihn sichtbar) */
  my_note?: string | null;
  /** Support hat mindestens eine Antwort (Mail) gesendet */
  has_outbound_reply?: boolean;
  last_reply_author_login?: string | null;
  last_reply_at?: string | null;
  /** Melder muss auf Rückfrage oder Support-Antwort reagieren */
  action_required?: boolean;
}

export interface SupportTicketsResponse {
  admin: boolean;
  items: SupportTicket[];
  tags: SupportTag[];
}

export interface TicketHistoryEntry {
  changed_at?: string;
  changed_by_display?: string;
  changed_by_login?: string;
  old_status?: string;
  new_status?: string;
  note?: string;
}

export interface TicketReply {
  id?: number;
  message?: string;
  author_display?: string;
  author_login?: string;
  created_at?: string;
}

export interface TicketAttachment {
  id: number;
  filename?: string;
  name?: string;
  url?: string;
  size_bytes?: number;
  content_type?: string | null;
  reply_id?: number | null;
}

export function ticketLevelAttachments(attachments: TicketAttachment[] | null | undefined): TicketAttachment[] {
  return (attachments || []).filter((att) => att.reply_id == null);
}

export function replyAttachments(
  attachments: TicketAttachment[] | null | undefined,
  replyId: number | null | undefined,
): TicketAttachment[] {
  if (replyId == null) return [];
  return (attachments || []).filter((att) => att.reply_id === replyId);
}

export interface TicketHistoryResponse {
  ticket: SupportTicket;
  tags?: SupportTag[];
  history?: TicketHistoryEntry[];
  replies?: TicketReply[];
  attachments?: TicketAttachment[];
}

export interface AiResolutionSuggestion {
  ticket_id: number;
  title?: string;
  confidence?: number;
  reason?: string;
  confirm_message?: string;
  suggested_status?: string;
  suggested_todo_state?: string;
}

export interface AiCachedItem {
  payload?: Record<string, unknown>;
  created_at?: string;
  stale?: boolean;
}

export interface AiCachedResponse {
  ai_enabled?: boolean;
  items?: {
    triage?: AiCachedItem;
    reply_draft?: AiCachedItem;
    resolution_check?: AiCachedItem;
  };
}

export function attachmentUrl(att: TicketAttachment): string {
  if (att.url) return `${API_BASE}${att.url}`;
  return `${API_BASE}/support/attachment/${att.id}`;
}

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif|avif)$/i;

export function isTicketAttachmentImage(att: TicketAttachment): boolean {
  const ct = String(att.content_type || "").toLowerCase();
  if (ct.startsWith("image/")) return true;
  const name = String(att.filename || att.name || "").toLowerCase();
  return IMAGE_EXT.test(name);
}

export function attachmentLabel(att: TicketAttachment): string {
  return att.filename || att.name || `Anhang ${att.id}`;
}

export async function listSupportTickets(onlyOpen: boolean): Promise<SupportTicketsResponse> {
  const q = onlyOpen ? "true" : "false";
  return api.get<SupportTicketsResponse>(`/support/tickets?only_open=${q}`);
}

export async function listAiResolutionSuggestions(
  minConfidence = 0.6,
): Promise<{ items: AiResolutionSuggestion[] }> {
  return api.get(`/support/ai/resolution-suggestions?min_confidence=${minConfidence}`);
}

export async function fetchTicketHistory(ticketId: number): Promise<TicketHistoryResponse> {
  return api.get(`/support/ticket/${ticketId}/history`);
}

export async function createSupportTicket(form: FormData): Promise<unknown> {
  return api.postForm("/support/ticket", form);
}

export async function updateTicketStatus(
  ticketId: number,
  status: string,
  note = "",
): Promise<unknown> {
  return api.patch(`/support/ticket/${ticketId}/status`, { status, note });
}

export async function updateTicketTodo(
  ticketId: number,
  body: {
    todo_state?: string;
    todo_owner_login?: string;
    todo_note?: string | null;
    todo_due_at?: string | null;
  },
): Promise<unknown> {
  return api.patch(`/support/ticket/${ticketId}/todo`, body);
}

export async function updateTicketUrgency(
  ticketId: number,
  urgency: TicketUrgency | string,
): Promise<{ id: number; urgency: string }> {
  return api.patch(`/support/ticket/${ticketId}/urgency`, { urgency });
}

export async function fetchTicketMyNote(
  ticketId: number,
): Promise<{ ticket_id: number; note: string; updated_at?: string | null }> {
  return api.get(`/support/ticket/${ticketId}/my-note`);
}

export async function saveTicketMyNote(
  ticketId: number,
  note: string,
): Promise<{ ticket_id: number; note: string; updated_at?: string | null }> {
  return api.put(`/support/ticket/${ticketId}/my-note`, { note });
}

export async function markSupportTicketRead(
  ticketId: number,
): Promise<{ ticket_id: number; is_unread: boolean }> {
  return api.post(`/support/ticket/${ticketId}/read`);
}

export async function markSupportTicketUnread(
  ticketId: number,
): Promise<{ ticket_id: number; is_unread: boolean }> {
  return api.post(`/support/ticket/${ticketId}/unread`);
}

export interface TicketMetaDraft {
  status: string;
  statusNote?: string;
  todo_state: string;
  todo_owner_login: string;
  todo_due_at: string | null;
  todo_note: string;
  tagNames: string[];
}

/** Speichert geänderte Ticket-Metadaten (Status, To-Do, Tags). */
export async function saveTicketMetaChanges(
  ticketId: number,
  saved: TicketMetaDraft,
  draft: TicketMetaDraft,
): Promise<void> {
  const todoChanged =
    saved.todo_state !== draft.todo_state ||
    saved.todo_owner_login !== draft.todo_owner_login ||
    saved.todo_due_at !== draft.todo_due_at ||
    saved.todo_note !== draft.todo_note;

  if (todoChanged) {
    await updateTicketTodo(ticketId, {
      todo_state: draft.todo_state,
      todo_owner_login: draft.todo_owner_login,
      todo_due_at: draft.todo_due_at,
      todo_note: draft.todo_note.trim() || null,
    });
  }

  const savedTags = [...saved.tagNames].map((n) => n.trim().toLowerCase()).filter(Boolean).sort().join("|");
  const draftTags = [...draft.tagNames].map((n) => n.trim().toLowerCase()).filter(Boolean).sort().join("|");
  if (savedTags !== draftTags) {
    await updateTicketTags(ticketId, draft.tagNames);
  }

  if (saved.status !== draft.status) {
    await updateTicketStatus(ticketId, draft.status, draft.statusNote?.trim() || "");
  }
}

export type TicketQuickAction =
  | "assign_me"
  | "in_progress"
  | "waiting"
  | "resolved"
  | "closed"
  | "reopen";

export async function applyTicketQuickAction(
  ticketId: number,
  action: TicketQuickAction,
  opts: { currentLogin: string; note?: string; ownerLogin?: string; todoState?: string },
): Promise<void> {
  const note = opts.note?.trim() || "";
  const owner = opts.ownerLogin?.trim().toLowerCase() || "";

  switch (action) {
    case "assign_me":
      await updateTicketTodo(ticketId, {
        todo_state: "in_umsetzung",
        todo_owner_login: opts.currentLogin,
      });
      await updateTicketStatus(ticketId, "in_progress", note);
      return;
    case "in_progress":
      await updateTicketTodo(ticketId, {
        todo_state: opts.todoState || "in_umsetzung",
        todo_owner_login: owner || opts.currentLogin,
      });
      await updateTicketStatus(ticketId, "in_progress", note);
      return;
    case "waiting":
      await updateTicketStatus(ticketId, "waiting", note);
      return;
    case "resolved":
      await updateTicketTodo(ticketId, {
        todo_state: "erledigt",
        todo_owner_login: owner || opts.currentLogin,
      });
      await updateTicketStatus(ticketId, "resolved", note);
      return;
    case "closed":
      await updateTicketTodo(ticketId, {
        todo_state: "erledigt",
        todo_owner_login: owner || opts.currentLogin,
      });
      await updateTicketStatus(ticketId, "closed", note);
      return;
    case "reopen":
      await updateTicketTodo(ticketId, {
        todo_state: "eingang",
        todo_owner_login: owner,
      });
      await updateTicketStatus(ticketId, "open", note);
      return;
  }
}

export async function updateTicketMessage(ticketId: number, message: string): Promise<unknown> {
  return api.patch(`/support/ticket/${ticketId}`, { message });
}

export async function postTicketReply(ticketId: number, message: string): Promise<unknown> {
  return api.post(`/support/ticket/${ticketId}/reply`, { message });
}

export async function updateTicketTags(
  ticketId: number,
  tagNames: string[],
): Promise<unknown> {
  return api.put(`/support/ticket/${ticketId}/tags`, { tag_names: tagNames });
}

export async function fetchAiCached(ticketId: number): Promise<AiCachedResponse | null> {
  try {
    return await api.get<AiCachedResponse>(`/support/ticket/${ticketId}/ai/cached`);
  } catch {
    return null;
  }
}

export async function callAiTriage(ticketId: number): Promise<AiCachedItem> {
  return api.post(`/support/ticket/${ticketId}/ai/triage`);
}

export async function callAiReplyDraft(ticketId: number): Promise<AiCachedItem> {
  return api.post(`/support/ticket/${ticketId}/ai/reply-draft`);
}

export async function callAiResolutionCheck(ticketId: number): Promise<AiCachedItem> {
  return api.post(`/support/ticket/${ticketId}/ai/resolution-check`);
}

export async function confirmTicketResolved(ticketId: number, note = ""): Promise<unknown> {
  return api.post(`/support/ticket/${ticketId}/reporter/confirm`, { note });
}

export async function reopenSupportTicket(ticketId: number, message = ""): Promise<unknown> {
  return api.post(`/support/ticket/${ticketId}/reporter/reopen`, { message });
}

export async function markSupportTicketResolved(ticketId: number, note = ""): Promise<unknown> {
  return api.post(`/support/ticket/${ticketId}/reporter/resolve`, { note });
}

export async function fetchSupportActionRequiredCount(): Promise<number> {
  const data = await api.get<{ count?: number }>("/support/action-required-count");
  return Number(data?.count || 0);
}

export async function fetchSupportEingangCount(): Promise<number> {
  const data = await api.get<{ count?: number }>("/support/eingang-count");
  return Number(data?.count || 0);
}
